import { API } from "./APIClient";
import qs from "qs";
import { getCurrentUserId } from "../utils/reduxUser";


class CheckListAPIClass {
    async fetchChecklistID(date = new Date()) {
        const userId = getCurrentUserId();

        const response = await API.get(`/checklist/employee/${userId}/date/${date.toISOString().split("T")[0]}`);

        return {
            id: response.data.id,
            createdAt: response.data.created_at,
            checklistDate: response.data.checklist_date,
        };
    }

    async fetchDailyTasks(checklistId, categoryId = null) {
        const userId = getCurrentUserId();
        const query = qs.stringify({
            current_date: new Date().toISOString().split("T")[0],
            employee_id: userId,
        });

        let response = null;
        if (categoryId) {
            response = await API.get(`/checklist/${checklistId}/categories/${categoryId}/tasks?${query}`);
        } else {
            response = await API.get(`/checklist/${checklistId}/tasks?${query}`);
        }

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                createdAt: item.created_at,
                checklistId: item.checklist_id,
                employeeId: item.employee_id,
                taskText: item.task_text,
                countTotal: item.count_total,
                countDone: item.count_done,
                rewardExp: item.reward_exp,
                categoryName: item.category_name,
                startDate: item.start_date,
                endDate: item.end_date,
                assigneeDate: item.start_date,
                repeatEveryNDays: item.repeat_every_n_days,
                rewardReceived: item.reward_received,
                taskType: item.task_type,
                priority: item.priority - 1,
                note: item.note,
                photos: item.photo_urls,
            })
        };

        return result;
    }

    async completeStep(taskId, taskType, countDone, countTotal) {
        const query = qs.stringify({
            task_type: taskType,
        });

        const response = await API.patch(
            `/checklist/tasks/${taskId}/complete-step?${query}`,
            {
                count_done: countDone,
                count_total: countTotal,
            },
        );

        return {
            id: response.data.id,
            countDone: response.data.count_done,
            countTotal: response.data.count_total,
        };
    }

    async cancelCompletion(taskId, taskType) {
        const userId = getCurrentUserId();
        const res = await API.post(`/checklist/tasks/cancelCompletion?employee_id=${userId}`, {
            task_id: taskId,
            task_type: taskType,
            employee_id: userId
        });

        return  {
            levelInfo: {
                newExp: res.data.level_info.new_exp_info,
                newLevel: res.data.level_info.new_level_info
            },
            taskInfo: {
                id: res.data.task_info.id,
                createdAt: res.data.task_info.created_at,
                checklistId: res.data.task_info.checklist_id,
                employeeId: res.data.task_info.employee_id,
                taskText: res.data.task_info.task_text,
                countTotal: res.data.task_info.count_total,
                countDone: res.data.task_info.count_done,
                rewardExp: res.data.task_info.reward_exp,
                categoryName: res.data.task_info.category_name,
                startDate: res.data.task_info.start_date,
                endDate: res.data.task_info.end_date,
                assigneeDate: res.data.task_info.start_date,
                repeatEveryNDays: res.data.task_info.repeat_every_n_days,
                rewardReceived: res.data.task_info.reward_received,
                taskType: res.data.task_info.task_type,
                priority: res.data.task_info.priority - 1,
                note: res.data.task_info.note,
            }
        }
    }

    async receiveTaskExp(taskId, rewardExp, taskType) {
        const userId = getCurrentUserId();

        const response = await API.post(`/checklist/tasks/receiveReward`, {
            task_id: taskId,
            employee_id: userId,
            reward_exp: rewardExp,
            task_type: taskType,
        });

        return {
            levelInfo: {
                newExp: response.data.level_info.new_exp_info,
                newLevel: response.data.level_info.new_level_info
            },
            rewardReceived: response.data.task_info.reward_received,
        }
    }

    async fetchTaskData(taskId) {
        const userId = getCurrentUserId();
        const response = await API.get(`/checklist/tasks/${taskId}?employee_id=${userId}`);

        return {
            checklistId: response.data.checklist_id,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            taskType: response.data.task_type,
            priority: response.data.priority - 1,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            note: response.data.note,
            startDate: response.data.start_date,
            endDate: response.data.end_date,
            assigneeDate: response.data.start_date,
            repeatEveryNDays: response.data.repeat_every_n_days,
            categoryName: response.data.category_name,
            id: response.data.id,
            rewardReceived: response.data.reward_received,
            createdAt: response.data.created_at,
        }
    }

    async changeNote(taskId, taskType, note) {
        const userId = getCurrentUserId();

        const response = await API.patch(`/checklist/tasks/updateNote`, {
            task_id: taskId,
            task_type: taskType,
            employee_id: userId,
            note
        });

        return {
            checklistId: response.data.checklist_id,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            taskType: response.data.task_type,
            priority: response.data.priority - 1,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            note: response.data.note,
            startDate: response.data.start_date,
            endDate: response.data.end_date,
            assigneeDate: response.data.start_date,
            repeatEveryNDays: response.data.repeat_every_n_days,
            categoryName: response.data.category_name,
            id: response.data.id,
            rewardReceived: response.data.reward_received,
            createdAt: response.data.created_at,
        }
    }

    async fetchTaskImages(taskId) {
        const userId = getCurrentUserId();
        const response = await API.get(`/checklist/tasks/${taskId}/photos?employee_id=${userId}`);

        const result = [];
        for (const image of response.data.result) {
            result.push({
                id: image.id,
                src: image.photo_url
            })
        }

        return result;
    }

    async uploadTaskImage(taskId, image) {
        const userId = getCurrentUserId();
        const formData = new FormData();

        formData.append("task_id", taskId);
        formData.append("employee_id", userId);
        formData.append("filename", image.name);
        formData.append("photo", image);

        const response = await API.post(`/checklist/tasks/photos?employee_id=${userId}`, formData);

        return {
            id: response.data.id,
            src: response.data.photo_url,
        }
    }

    async deleteTaskImage(photoId) {
        const userId = getCurrentUserId();
        const response = await API.delete(`/checklist/tasks/photos/${photoId}?employee_id=${userId}`);

        return response.data;
    }

    async fetchUserTaskData(userId, taskId) {
        const response = await API.get(`/admin/checklist/tasks/${taskId}?employee_id=${userId}`);

        return {
            checklistId: response.data.checklist_id,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            taskType: response.data.task_type,
            priority: response.data.priority - 1,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            note: response.data.note,
            startDate: response.data.start_date,
            endDate: response.data.end_date,
            assigneeDate: response.data.start_date,
            repeatEveryNDays: response.data.repeat_every_n_days,
            categoryName: response.data.category_name,
            id: response.data.id,
            rewardReceived: response.data.reward_received,
            createdAt: response.data.created_at,
        };
    }

    async fetchUserTaskImages(userId, taskId) {
        const response = await API.get(`/admin/checklist/tasks/${taskId}/photos?employee_id=${userId}`);

        const result = [];
        for (const image of response.data.result) {
            result.push({
                id: image.id,
                src: image.photo_url
            })
        }

        return result;
    }

    async fetchChecklistCategories(cafeId) {
        const response = await API.get(`/admin/checklist/categories/cafe/${cafeId}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                text: item.category_name,
                image: item.image_url,
            });
        }

        return result;
    }

    async fetchUserChecklistCategories() {
        const userId = getCurrentUserId();
        const response = await API.get(`/checklist/categories?employee_id=${userId}`);

        const result = [];
        for (const item of response.data.result) {
            result.push({
                id: item.id,
                text: item.category_name,
                image: item.image_url,
            });
        }

        return result;
    }

    async createChecklistCategory(cafeId, categoryName) {
        const response = await API.post(`/admin/checklist/categories/createCategory`, {
            cafe_id: cafeId,
            category_name: categoryName,
        });

        return {
            id: response.data.id,
            categoryName: response.data.category_name,
            image: response.data.image_url,
        }
    }

    async deleteChecklistCategory(categoryId) {
        const response = await API.delete(`/admin/checklist/categories/${categoryId}`);
        return response.data;
    }

    async deleteAllChecklistCategories(cafeId) {
        const query = qs.stringify({
            cafe_id: cafeId,
        });

        const response = await API.delete(`/admin/checklist/categories/deleteAll?${query}`);
        return response.data;
    }

    async updateChecklistCategory(categoryId, categoryName) {
        const response = await API.patch(`/admin/checklist/categories/updateCategory`, {
            category_id: categoryId,
            category_name: categoryName,
        });

        return {
            id: response.data.id,
            text: response.data.category_name,
            image: response.data.image_url,
        }
    }

    async uploadChecklistCategoryImage(categoryId, file) {
        const formData = new FormData();

        formData.append("photo_file", file);
        formData.append("filename", file.name);

        const response = await API.post(`/admin/checklist/categories/${categoryId}/image`, formData);

        return {
            id: response.data.id,
            text: response.data.category_name,
            image: response.data.image_url,
        }
    }

    async deleteChecklistCategoryImage(categoryId) {
        const response = await API.delete(`/admin/checklist/categories/${categoryId}/image`);
        return response.data;
    }

    async createDailyReport(date, objectName, shift, supervisor, comment, reportTitle) {
        const userId = getCurrentUserId();

        const response = await API.post(`/checklist/reports/daily`, {
            employee_id: userId,
            checklist_date: date.toISOString().split("T")[0],
            object_name: objectName,
            shift: shift,
            supervisor: supervisor,
            comment: comment,
            report_title: reportTitle,
        });

        return response.data;
    }

    async fetchDailyReportObjectNames(cafeId) {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            employee_id: userId,
            cafe_id: cafeId,
        });

        const response = await API.get(`/checklist/reports/object-names?${query}`);
        return response.data.object_names;
    }

    async fetchReportsList(date) {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            employee_id: userId,
            report_date: date?.toISOString().split("T")[0]
        });

        const response = await API.get(`/checklist/reports/list?${query}`);

        const result = [];
        for (const item of response.data.reports) {
            result.push({
                filename: item.filename,
                format: item.format,
            });
        }

        return result;
    }

    async fetchReportDownloadLink(filename) {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            employee_id: userId,
            filename,
        });

        const response = await API.get(`/checklist/reports/download?${query}`);

        return response.data.url;
    }

    async fetchUserReportsList(userId, date) {
        const query = qs.stringify({
            employee_id: userId,
            report_date: date?.toISOString().split("T")[0]
        });

        const response = await API.get(`/admin/checklist/reports/list?${query}`);

        const result = [];
        for (const item of response.data.reports) {
            result.push({
                filename: item.filename,
                format: item.format,
            });
        }

        return result;
    }

    async fetchUserReportDownloadLink(userId, filename) {
        const query = qs.stringify({
            employee_id: userId,
            filename,
        });

        const response = await API.get(`/admin/checklist/reports/download?${query}`);

        return response.data.url;
    }

    async fetchCafeReportsList(cafeId) {
        const response = await API.get(`/admin/checklist/reports/summary/list?cafe_id=${cafeId}`);

        const result = [];
        for (const item of response.data.reports) {
            result.push({
                id: item.id,
                periodStart: item.period_start,
                periodEnd: item.period_end,
                createdAt: new Date(item.created_at),
                filename: item.original_filename,
                objectName: item.object_name
            });
        }

        return result;
    }

    async fetchAdminCafeReportObjectNames(cafeId) {
        const response = await API.get(`/admin/checklist/reports/object-names?cafe_id=${cafeId}`);
        return response.data.object_names;
    }

    async fetchCafeReportDownloadLink(cafeId, reportId) {
        const response = await API.get(`/admin/checklist/reports/summary/${reportId}/download?cafe_id=${cafeId}`);

        return response.data.url;
    }

    async deleteCafeReport(cafeId, reportId) {
        const response = await API.delete(`/admin/checklist/reports/summary/${reportId}?cafe_id=${cafeId}`);

        return response.data;
    }

    async createCafeReport(cafeId, periodStart, periodEnd, objectNames) {
        const response = await API.post(`/admin/checklist/reports/summary`, {
            cafe_id: cafeId,
            start_date: periodStart.toISOString().split("T")[0],
            end_date: periodEnd.toISOString().split("T")[0],
            object_names: objectNames
        }, {
            skipEmployeeIdInjection: true
        });

        return {
            id: response.data.report_id,
            url: response.data.url,
            filename: response.data.filename,
        };
    }

    async updateTask(taskId, taskType, taskText, priority, countTotal, countDone, rewardExp, categoryName, startDate, endDate) {
        const response = await API.patch(`/admin/checklist/tasks/updateTask`, {
            task_id: taskId,
            task_type: taskType,
            task_text: taskText,
            priority: priority + 1,
            count_total: countTotal,
            count_done: countDone,
            reward_exp: rewardExp,
            category_name: categoryName,
            start_date: startDate.toISOString().split("T")[0],
            end_date: endDate.toISOString().split("T")[0]
        });

        return {
            checklistId: response.data.checklist_id,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            taskType: response.data.task_type,
            priority: response.data.priority - 1,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            note: response.data.note,
            startDate: response.data.start_date,
            endDate: response.data.end_date,
            assigneeDate: response.data.start_date,
            repeatEveryNDays: response.data.repeat_every_n_days,
            categoryName: response.data.category_name,
            id: response.data.id,
            rewardReceived: response.data.reward_received,
            createdAt: response.data.created_at,
        };
    }

    async fetchReccurentTaskData(userId, taskId) {
        const response = await API.get(`/admin/checklist/reccurentTasks/${taskId}?employee_id=${userId}`);

        return {
            id: response.data.id,
            createdAt: response.data.created_at,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            categoryName: response.data.category_name,
            repeatEveryNDays: response.data.repeat_every_n_days,
            priority: response.data.priority - 1
        };
    }

    async updateReccurentTask(taskId, taskText, priority, countTotal, rewardExp, categoryName, repeatEveryNDays) {
        const response = await API.patch(`/admin/checklist/tasks/updateReccurentTask`, {
            task_id: taskId,
            task_text: taskText,
            priority: priority + 1,
            count_total: countTotal,
            count_done: 0,
            reward_exp: rewardExp,
            category_name: categoryName,
            repeat_every_n_days: repeatEveryNDays
        });

        return {
            id: response.data.id,
            createdAt: response.data.created_at,
            employeeId: response.data.employee_id,
            taskText: response.data.task_text,
            countTotal: response.data.count_total,
            countDone: response.data.count_done,
            rewardExp: response.data.reward_exp,
            categoryName: response.data.category_name,
            repeatEveryNDays: response.data.repeat_every_n_days,
            priority: response.data.priority - 1
        };
    }
}

const CheckListAPI = new CheckListAPIClass();
export default CheckListAPI;
